import React, { useCallback, useEffect, useMemo, useState } from 'react';
import VideoTrimmingControls from '../../routes/newVideo/VideoTrimmingControls/VideoTrimmingControls';
import MultipleVideosPlayer from '../MultipleVideosPlayer/MultipleVideosPlayer';
import VideosEditorSlider from '../VideosEditorSlider/VideosEditorSlider';
import styles from './ClipOrganizerPlayer.module.scss';
import cx from 'classnames';
import { useTrimContext } from '../../context/VideoTrimContex';
import RecordClipTrigger from '../RecordClipTrigger/RecordClipTrigger';
import VideoService from '../../service/VideoService';
import { timeStampToMs } from '../../utils/TimeUtil';
import { useTranslation } from 'react-i18next';
import { useBackgroundMusicContext } from '../../context/BackgroundMusicContext';
import { useZoomContext } from '../../context/ZoomContext';
import { ZoomVideoControls } from '../../features/ZoomVideo';
import UploadClipTab from '../UploadClipTab/UploadClipTab';
import { useUIContext } from '../../context/UIContext';
import UploadClipModal from '../../features/Editor/components/UploadClipModal/UploadClipModal';
import Modal from '../Modal';
import AvatarModal from '../../features/Editor/components/AvatarModal/AvatarModal';
import { useUserContext } from 'engage/context/UserContext';
import { ScenesType } from '../../../common/apis/CmsAPI';

const ClipOrganizerPlayer = ({
  video,
  storyId,
  scenes,
  transitions,
  sceneIndex,
  switchingTrimming,
  saving,
  intro,
  outro,
  logo,
  draft,
  setSceneIndex,
  handleEnded,
  handleSliderChange,
  className,
  hasChanged,
  updateSceneData = () => { },
  isStopBackgroundMusic,
  userPermissions,
  videoElements,
  currentPlayerKey,
  setSeekPlayerTo,
  miniRightSidebar,
  isRecordingGeneral,
  isEditingRightSidebar,
  handleRecordScene,
  innerRef,
  handleCancelRecording,
  handleGenerateAvatarClip,
  videoConfig,
}) => {
  const [customTiming, setCustomTiming] = useState({ progress: 0, duration: 0 });
  const [waveform, setWaveform] = useState(null);
  const [uploadClipInfo, setUploadClipInfo] = useState({});
  const [isRecording, setIsRecording] = useState(false);
  const [avatarForClip, setAvatarForClip] = useState({});
  const newLocalSceneSelected = useMemo(() => scenes.some((scene, index) => scene.isNew && index == sceneIndex), [sceneIndex, scenes]);

  const { forcedPlayerStyles, forcedMinSlider } = useUIContext();

  const { t } = useTranslation();

  const sceneCouldBeRerecorded = useMemo(() => video?.scenes?.find(item => item.id === scenes[sceneIndex]?.id) !== undefined, [
    video,
    scenes,
    sceneIndex,
  ]);

  const currentPlayer = videoElements?.find(item => item.key === currentPlayerKey)?.video;

  const handleUpdateCustoms = input => {
    setCustomTiming(prevState => ({ ...prevState, ...input }));
  };

  const { isAvatarEnabled } = useUserContext();

  const avatarDisabledTooltip = useMemo(() => {
    if (newLocalSceneSelected) {
      return '';
    }
    if (scenes[sceneIndex]?.script?.length === 0) {
      return t('clip_organizer.generate_avatar.avatar_disabled_tooltip');
    }
    if (scenes[sceneIndex]?.scriptChanged) {
      return t('clip_organizer.generate_avatar.avatar_save_script_tooltip');
    }
    return '';
  }, [scenes, sceneIndex, newLocalSceneSelected, t]);

  const { trimmingOn } = useTrimContext();

  const { isBackgroundMusicOpened, isApplyAllTrackEnabled } = useBackgroundMusicContext();

  const { isZoomOpened } = useZoomContext();

  const isShowOnlyCurrentScene = useMemo(() => isBackgroundMusicOpened && !isApplyAllTrackEnabled, [
    isBackgroundMusicOpened,
    isApplyAllTrackEnabled,
  ]);

  const isShowVideoSlider = useMemo(() => !trimmingOn && !switchingTrimming && !isZoomOpened, [
    trimmingOn,
    switchingTrimming,
    isZoomOpened,
  ]);

  const confirmationFromUser = (shouldConfirm) => {
    if (shouldConfirm) {
      return confirm(t('clip_organizer.confirm.rerecord'));
    }
    return true;
  }

  const handleRecord = useCallback((hasVideo = false) => () => {
    if (confirmationFromUser(hasVideo)) {
      handleRecordScene({ sceneId: scenes[sceneIndex]?.id, script: scenes[sceneIndex]?.script });
    }
  },
    [scenes, sceneIndex]
  );

  const handleOpenUploadClipModal = useCallback((hasVideo = false) =>
    () => {
      if (confirmationFromUser(hasVideo)) {
        setUploadClipInfo({ sceneId: scenes[sceneIndex]?.id, storyId, isOpen: true });
      }
    },
    [scenes, sceneIndex]
  );

  const handleUploadClip = ([uploadedScene]) => {
    setUploadClipInfo({});

    // The 'storage' event doesn't fire if the localStorage is changed on the same tab,
    // so we have to trigger it manually
    window.dispatchEvent(
      new StorageEvent('storage', {
        key: 'draftSceneId',
        oldValue: uploadedScene?.id,
        newValue: null,
      })
    );
  };

  const onAvatarSelected = useCallback(([{ avatarId, voiceId }]) => {
    handleGenerateAvatarClip(avatarForClip.sceneId, avatarId, voiceId);
  }, [avatarForClip])

  const handleGenerateAvatar = useCallback((hasVideo = false) => (e) => {
    e.stopPropagation();
    if (confirmationFromUser(hasVideo)) {
      setAvatarForClip({ sceneId: scenes[sceneIndex]?.id, isOpen: true });
    }
  }, [scenes, sceneIndex]);

  const onCancelRecording = useCallback(() => {
    setIsRecording(false);
    handleCancelRecording();
  });

  const filteredScenes = useMemo(() => scenes.filter(item => !item.hidden), [scenes]);

  useEffect(() => {
    const storageListener = async ({ key, newValue, oldValue }) => {
      if (key !== 'draftSceneId') {
        return;
      }

      if (newValue) {
        updateSceneData(newValue, { processing: false, recording: true });
      } else {
        try {
          const { scenes: updatedScenes } = await VideoService.getVideoDetails(storyId, ScenesType.REGULAR);
          const sceneOldValueId = scenes.find(({ id }) => id === oldValue);
          const recordedScene = updatedScenes.find(({ id }) => id === oldValue);

          const checkRunningJobs = await VideoService.checkSceneJobs(storyId, oldValue);

          if (checkRunningJobs.hasRunningJobs && recordedScene?.video) {
            recordedScene.trimStart = timeStampToMs(recordedScene.trimStart);
            recordedScene.trimEnd = timeStampToMs(recordedScene.trimEnd);
            updateSceneData(
              oldValue,
              { ...recordedScene, storyId, recording: false, hidden: false },
              checkRunningJobs.runningJobs,
              sceneOldValueId?.resources?.length > 0
            );
          } else {
            updateSceneData(oldValue, { recording: false });
          }
        } catch (e) {
          console.log('draftSceneId error:', e);
          updateSceneData(oldValue, { processing: false, recording: false });
        }
      }

      setIsRecording(!!newValue);
    };

    window.addEventListener('storage', storageListener);
    return () => window.removeEventListener('storage', storageListener);
  }, [scenes.length]);

  const listenerFunc = event => {
    const extensionId = process.env.EXT_ID;
    // We only accept messages from this window to itself [i.e. not from any iframes]
    if (event.source !== window) return;

    if (event.data.type && event.data.type === `${extensionId}-extension`) {
      if (event.data.message === 'UPDATE_DRAFT_SCENE_STATUS') {
        const sceneId = event.data.sceneId;
        setIsRecording(!!sceneId);

        if (sceneId) {
          localStorage.setItem('draftSceneId', sceneId);
        } else {
          localStorage.removeItem('draftSceneId');
        }
      }

      if (event.data.message === 'START_UPLOAD_A_DRAFT_SCENE') {
        const sceneId = event.data.sceneId;
        setIsRecording(!!sceneId);
        if (sceneId) {
          localStorage.setItem('draftSceneId', sceneId);

          // The 'storage' event doesn't fire if the localStorage is changed on the same tab,
          // so we have to trigger it manually
          window.dispatchEvent(
            new StorageEvent('storage', {
              key: 'draftSceneId',
              oldValue: null,
              newValue: sceneId,
            })
          );
        }
      }

      if (event.data.message === 'CANCEL_UPLOAD_A_DRAFT_SCENE') {
        setIsRecording(false);
        const oldValue = localStorage.getItem('draftSceneId');
        localStorage.removeItem('draftSceneId');

        // The 'storage' event doesn't fire if the localStorage is changed on the same tab,
        // so we have to trigger it manually
        window.dispatchEvent(
          new StorageEvent('storage', {
            key: 'draftSceneId',
            oldValue,
            newValue: null,
          })
        );
      }
    }
  };

  const onSliderChange = useCallback(
    (newVideoIndex, seekNewVideoTo, callback) => {
      handleSliderChange(newVideoIndex, seekNewVideoTo);
      setSeekPlayerTo({ newVideoIndex, value: seekNewVideoTo });
      callback(seekNewVideoTo, newVideoIndex);
    },
    [handleSliderChange, setSeekPlayerTo]
  );

  const fetchWaveform = async waveformUrl => {
    try {
      const response = await fetch(waveformUrl);
      const data = await response.json();
      setWaveform(data);
    } catch (error) {
      console.error('Error fetching waveform:', error);
    }
  };

  useEffect(() => {
    window.addEventListener('message', listenerFunc);

    const extensionId = process.env.EXT_ID;
    window.postMessage({ type: extensionId, message: 'GET_DRAFT_SCENE_STATUS' }, '*');

    return () => window.removeEventListener('message', listenerFunc);
  }, []);

  useEffect(() => {
    if (!trimmingOn && !isZoomOpened) {
      setWaveform(null);
    }
  }, [trimmingOn, isZoomOpened]);

  useEffect(() => {
    if (scenes && scenes[sceneIndex] && (trimmingOn || isZoomOpened)) {
      const waveformUrl = scenes[sceneIndex]?.waveform;
      if (waveformUrl) {
        fetchWaveform(waveformUrl);
      } else {
        setWaveform(null);
      }
    }
  }, [trimmingOn, isZoomOpened, scenes, sceneIndex]);

  useEffect(() => {
    switchingTrimming && setWaveform(null);
  }, [switchingTrimming]);

  return (
    <div ref={innerRef} className={styles.wrapper} style={forcedPlayerStyles}>
      <MultipleVideosPlayer
        videoElements={videoElements}
        currentPlayerKey={currentPlayerKey}
        scenes={filteredScenes}
        transitions={transitions}
        videoIndex={sceneIndex}
        handleEnded={handleEnded}
        className={cx({ [styles.trimmingShown]: trimmingOn || switchingTrimming || isZoomOpened }, className)}
        setSceneIndex={setSceneIndex}
        customTiming={customTiming}
        logo={logo}
        intro={intro}
        outro={outro}
        isDraft={draft}
        trimmingOn={trimmingOn}
        draftComponent={
          <RecordClipTrigger
            actions={[
              { label: t('storyboard.editor.record_button'), onClick: handleRecord(), disabled: (hasChanged && newLocalSceneSelected) || isRecordingGeneral },
              { label: t('clip_organizer.add_modal.upload'), onClick: handleOpenUploadClipModal(), disabled: (hasChanged && newLocalSceneSelected) || isRecordingGeneral },
              ...(isAvatarEnabled ? [{ label: t('clip_organizer.add_modal.avatar'), onClick: handleGenerateAvatar(), disabled: (hasChanged && newLocalSceneSelected) || isRecordingGeneral || !scenes[sceneIndex]?.script?.length || scenes[sceneIndex]?.scriptChanged, disabledTooltip: avatarDisabledTooltip }] : [])
            ]}
            isRecording={isRecording}
            message={newLocalSceneSelected && hasChanged ? t('clip_organizer.save_to_record_message') : t('storyboard.editor.clip_status.not_recorded')}
            handleCancel={onCancelRecording}
          />
        }
        rerecordComponent={
          <RecordClipTrigger
            actions={[
              { label: t('storyboard.editor.record_button'), onClick: handleRecord(true), disabled: (hasChanged && !sceneCouldBeRerecorded) || isRecordingGeneral },
              { label: t('clip_organizer.add_modal.upload'), onClick: handleOpenUploadClipModal(true), disabled: (hasChanged && !sceneCouldBeRerecorded) || isRecordingGeneral },
              ...(isAvatarEnabled ? [{ label: t('clip_organizer.add_modal.avatar'), onClick: handleGenerateAvatar(true), disabled: (hasChanged && !sceneCouldBeRerecorded) || isRecordingGeneral || !scenes[sceneIndex]?.script?.length || scenes[sceneIndex]?.scriptChanged, disabledTooltip: avatarDisabledTooltip }] : [])
            ]}
            isRecording={isRecording}
            message={!sceneCouldBeRerecorded && hasChanged ? t('clip_organizer.save_to_record_message') : ''}
            disableClassName={styles.disableRerecordButton}
            handleCancel={onCancelRecording}
          />
        }
        isStopBackgroundMusic={isStopBackgroundMusic}
        userPermissions={userPermissions}
        miniRightSidebar={miniRightSidebar}
        isEditingRightSidebar={isEditingRightSidebar}
        videoConfig={videoConfig}
      />

      {isShowVideoSlider && (
        <VideosEditorSlider
          scenes={isShowOnlyCurrentScene ? [scenes[sceneIndex]] : scenes}
          selectedClip={isShowOnlyCurrentScene ? 0 : sceneIndex}
          playedSeconds={(currentPlayer?.currentTime ?? 0) / (scenes[sceneIndex]?.playbackRate || 1)}
          originalSceneIndex={isShowOnlyCurrentScene ? sceneIndex : null}
          handleSliderChange={onSliderChange}
          updateTiming={handleUpdateCustoms}
          saving={saving}
        />
      )}

      {(trimmingOn || switchingTrimming) && (
        <VideoTrimmingControls
          trimStart={scenes[sceneIndex]?.trimStart / (scenes[sceneIndex]?.playbackRate || 1)}
          trimEnd={scenes[sceneIndex]?.trimEnd / (scenes[sceneIndex]?.playbackRate || 1)}
          waveform={waveform}
          className={styles.trimmingContainer}
          min={forcedMinSlider}
        />
      )}

      {isZoomOpened && (
        <ZoomVideoControls
          activeScene={scenes[sceneIndex]}
          videoDuration={(scenes[sceneIndex]?.trimEnd - scenes[sceneIndex]?.trimStart || 0) / (scenes[sceneIndex]?.playbackRate || 1)}
          waveform={waveform}
          min={forcedMinSlider}
        />
      )}
      <UploadClipModal open={uploadClipInfo.isOpen} onClose={() => setUploadClipInfo({})}>
        <UploadClipTab sceneToUploadInfo={uploadClipInfo} onUploadDone={handleUploadClip} />
      </UploadClipModal>
      <Modal open={avatarForClip?.isOpen} onClose={() => setAvatarForClip({})}>
        <AvatarModal open={avatarForClip?.isOpen} scenes={scenes?.filter(item => item.id == avatarForClip?.sceneId)} sceneId={avatarForClip?.sceneId} onClose={() => setAvatarForClip({})} onSelect={onAvatarSelected} />
      </Modal>
    </div>
  );
};

export default ClipOrganizerPlayer;
